import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  FormDialogActions,
  FormDialogTitle,
  FormCancelButton,
  FormSubmitButton,
} from '@marageti/z4-lib';
import { CountryVisited } from '@marageti/z4-sdk/lib/people';
import { CountryCodeMap } from '@marageti/z4-sdk/lib/standards';
import CountrySelector from '../country-lookup-input';

export type TravelerProfileCountriesModalProps = {
  open: boolean;
  initialCountries: CountryVisited[];
  countryCodes: CountryCodeMap | undefined;
  onClose: () => void;
  onSubmit: (codes: CountryVisited[]) => void;
};

const CURRENT_YEAR = new Date().getFullYear();

const TravelerProfileCountriesModal = ({
  open,
  initialCountries,
  countryCodes,
  onClose,
  onSubmit,
}: TravelerProfileCountriesModalProps) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('desktop'));
  const [localCountries, setLocalCountries] = useState<CountryVisited[]>(initialCountries);
  const [newCountry, setNewCountry] = useState<string>('');
  const [newYear, setNewYear] = useState<string>('');
  const [yearError, setYearError] = useState<string>('');

  useEffect(() => {
    if (open) {
      setLocalCountries(initialCountries);
      setNewCountry('');
      setNewYear('');
      setYearError('');
    }
  }, [open, initialCountries]);

  const validateYear = (value: string): string => {
    const n = parseInt(value, 10);
    if (!value) return 'Required';
    if (!Number.isInteger(n) || n < 1900 || n > CURRENT_YEAR) return `Enter a year between 1900 and ${CURRENT_YEAR}`;
    return '';
  };

  const handleAdd = () => {
    const error = validateYear(newYear);
    if (error) {
      setYearError(error);
      return;
    }
    if (!newCountry) return;
    const year = parseInt(newYear, 10);
    setLocalCountries((prev) => [
      ...prev,
      { country: newCountry, date: new Date(Date.UTC(year, 0, 1)), zicasso: false },
    ]);
    setNewCountry('');
    setNewYear('');
    setYearError('');
  };

  const handleDelete = (index: number) => {
    setLocalCountries((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    onSubmit(localCountries);
    onClose();
  };

  const isAddDisabled = !newCountry || !newYear;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      fullScreen={fullScreen}
    >
      <FormDialogTitle handleClose={onClose} sx={{ pb: 0.5 }}>
        Countries Visited
      </FormDialogTitle>
      <DialogContent>
        <Typography variant="body" color="primary" sx={{ mb: 2 }}>
          These preferences improve trip matching.
        </Typography>

        {localCountries.length > 0 && (
          <Box sx={{ maxHeight: { desktop: 500 }, overflowY: 'auto', mb: 3 }}>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {localCountries
                .map((cv, index) => ({ cv, index }))
                .sort((a, b) => {
                  const yearA = a.cv.date ? new Date(a.cv.date).getUTCFullYear() : 0;
                  const yearB = b.cv.date ? new Date(b.cv.date).getUTCFullYear() : 0;
                  return yearB - yearA;
                })
                .map(({ cv, index }) => {
                  const year = cv.date ? new Date(cv.date).getUTCFullYear() : null;
                  const countryLabel = countryCodes?.[cv.country] ?? cv.country;
                  const label = year ? `${countryLabel} (${year})` : countryLabel;
                  return (
                    <Chip
                      // eslint-disable-next-line react/no-array-index-key
                      key={`${cv.country}-${index}`}
                      label={label}
                      onDelete={cv.zicasso ? undefined : () => handleDelete(index)}
                      sx={{ backgroundColor: (t) => (cv.zicasso ? t.palette.common.info50 : t.palette.common.beige50) }}
                    />
                  );
                })}
            </Stack>
          </Box>
        )}

        <Typography variant="captionBold" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          Add a country
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" useFlexGap>
          <CountrySelector
            label="Country"
            value={newCountry}
            onChange={setNewCountry}
          />
          <TextField
            label="Year"
            type="number"
            value={newYear}
            onChange={(e) => {
              setNewYear(e.target.value);
              if (yearError) setYearError(validateYear(e.target.value));
            }}
            onBlur={() => { if (newYear) setYearError(validateYear(newYear)); }}
            inputProps={{ min: 1900, max: CURRENT_YEAR }}
            error={!!yearError}
            sx={{ width: 150, mt: 2.5, mb: 1.25 }}
          />
          <Button
            variant="outlined"
            onClick={handleAdd}
            disabled={isAddDisabled}
            sx={{
              height: 43, whiteSpace: 'nowrap', mt: 2.5, mb: 1.25,
            }}
          >
            Add
          </Button>
        </Stack>
        {yearError && (
          <Typography variant="caption" color="error" sx={{ mt: -1, display: 'block', textAlign: 'right' }}>
            {yearError}
          </Typography>
        )}
      </DialogContent>
      <FormDialogActions>
        <FormCancelButton onClick={onClose} />
        <FormSubmitButton onClick={handleSubmit}>
          Save Countries
        </FormSubmitButton>
      </FormDialogActions>
    </Dialog>
  );
};

export default TravelerProfileCountriesModal;
